import React, { createContext, useCallback, useContext, useState } from "react";

// Roving-tabindex keyboard navigation for the folder pickers (spec §5.10 P0:
// "the promotion/move picker is keyboard-accessible — roving tabindex + key
// handlers"). The move picker (MoveDialog) and the promotion picker
// (AddToProjectButton) both render a recursive, lazily-loaded folder tree, so
// the nav logic lives here once and both consume it.
//
// Roving tabindex means exactly one row sits in the tab order at a time; Tab
// enters/exits the tree as a unit and Arrow keys move focus within it. Vertical
// movement (Up/Down/Home/End) is resolved off the live DOM order of rendered
// `[role="treeitem"]` rows so expanded children participate without a flattened
// model. Horizontal movement (Left/Right collapse/expand) and selection
// (Enter/Space) stay per-row, since expansion state is local to each row.

interface TreeNavValue {
  activeId: number | null;
  setActiveId: (id: number) => void;
}

const TreeNavContext = createContext<TreeNavValue | null>(null);

export const TreeNavProvider = TreeNavContext.Provider;

/**
 * Props a tree row spreads onto its focusable element so it participates in
 * roving tabindex: only the active row is tabbable, focusing a row claims the
 * roving slot, and the folder id is exposed for the container's Arrow handler.
 */
export const useTreeNavItem = (folderId: number) => {
  const ctx = useContext(TreeNavContext);
  const isActive = ctx ? ctx.activeId === folderId : false;
  return {
    tabIndex: isActive ? 0 : -1,
    onFocus: () => ctx?.setActiveId(folderId),
    "data-folder-id": folderId,
  };
};

const VERTICAL_KEYS = ["ArrowDown", "ArrowUp", "Home", "End"];

/**
 * Container-level controller. `firstId` is the id of the first rendered row, so
 * the tree is tabbable before the user has moved (the first row adopts the
 * roving slot until then). Spread `onKeyDown` on the `role="tree"` element and
 * pass `{ activeId, setActiveId }` to `TreeNavProvider`.
 */
export const useTreeKeyboardNav = (firstId: number | null) => {
  const [activeId, setActiveIdState] = useState<number | null>(null);
  const setActiveId = useCallback((id: number) => setActiveIdState(id), []);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (!VERTICAL_KEYS.includes(e.key)) return;
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>('[role="treeitem"]'),
    );
    if (items.length === 0) return;
    e.preventDefault();
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? items.indexOf(active) : -1;
    let nextIdx: number;
    if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = items.length - 1;
    else if (e.key === "ArrowDown")
      nextIdx = idx < 0 ? 0 : Math.min(idx + 1, items.length - 1);
    else nextIdx = idx < 0 ? 0 : Math.max(idx - 1, 0);
    const el = items[nextIdx];
    const id = Number(el.dataset.folderId);
    if (!Number.isNaN(id)) setActiveIdState(id);
    el.focus();
  }, []);

  // Until the user moves, the first rendered row carries the roving slot.
  return { activeId: activeId ?? firstId, setActiveId, onKeyDown };
};
